/* eslint-disable import/prefer-default-export */
import optionsMobileData from '@/mobile/utils/options.json';
import optionsData from '@/utils/options.json';
import { Constants } from './constants';

/**
 * 日付・文字列整形と options.json 参照をまとめた汎用ユーティリティ群。
 * 画面側で同じ整形ロジックを重複させないため、表示用の共通変換をここへ集約している。
 */

/**
 * 空文字確認
 * @param {undefined | null | string | string[]}variable 検索する変数
 * @returns {boolean} true: 空文字
 */
export function isEmpty(variable: undefined | null | string | string[]): boolean {
  const result: boolean = !!(
    variable === undefined ||
    variable === null ||
    variable === '' ||
    (Array.isArray(variable) === true && (variable as string[] | string[]).length < 1)
  );
  return result;
}

/**
 * 郵便番号の整形
 * @export
 * @param {string}zipCode 整形元の郵便番号: 1230005
 * @returns {string} 整形済み郵便番号: 123-0005
 */
export function formatZipCode(zipCode: string): string {
  if (isEmpty(zipCode) === true || zipCode.length < 7) {
    return '';
  }
  const result: string = `${zipCode.slice(0, 3)}-${zipCode.slice(3)}`;
  return result;
}

/**
 * サービス開始前を表す日付かどうか
 * @export
 * @param {Date}target 検査する日付
 * @returns {boolean} true:サービス開始前
 */
export function isBeforeStartDate(target: Date): boolean {
  if (target.getFullYear() === 9999 && target.getMonth() + 1 === 12 && target.getDate() === 31) {
    return true;
  }
  return false;
}

/**
 * 日付の整形
 * @export
 * @param {string}origin 整形元の文字列: 2022-01-23
 * @returns {string} 整形済み日付: 2022年1月23日
 */
export function formatDate(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 10) {
    return origin;
  }
  const originDate: Date = new Date(origin);
  // 9999-12-31は「-」にする
  if (isBeforeStartDate(originDate)) {
    return '-';
  }
  const result: string = `${originDate.getFullYear()}年${
    originDate.getMonth() + 1
  }月${originDate.getDate()}日`;
  return result;
}

/**
 * 日付の整形
 * @export
 * @param {Date} origin 日付
 * @returns {string} 整形済み日付: 2022年1月23日
 */
export function formatDateForDisplay(origin: Date): string {
  const year = origin.getFullYear();
  const month = origin.getMonth() + 1;
  const day = origin.getDate();

  // Date オブジェクトから直接表示文言を作り、文字列再パースを避ける。
  return `${year}年${month}月${day}日`;
}

/**
 * 日付の整形
 * @export
 * @param {string} origin 整形元の文字列: 20220123
 * @returns {string} 整形済み日付: 2022年1月23日
 */
export function formatDateStringToJapanese(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 8) {
    return origin;
  }

  const year = parseInt(origin.substring(0, 4), 10);
  const month = parseInt(origin.substring(4, 6), 10);
  const day = parseInt(origin.substring(6, 8), 10);

  return `${year}年${month}月${day}日`;
}

/**
 * 18歳以上かどうかを判定する
 * @export
 * @param {string} year 生年（西暦）
 * @param {string} month 生月
 * @param {string} day 生日
 * @returns {boolean} true: 18歳以上、false: 18歳未満
 */
export function checkOver18(year: string, month: string, day: string): boolean {
  const birthDate = new Date(Number(year), Number(month) - 1, Number(day));
  const currentDate = new Date();

  // 18年前の現在と同じ日付
  const date18YearsAgo = new Date(
    currentDate.getFullYear() - 18,
    currentDate.getMonth(),
    currentDate.getDate()
  );

  return date18YearsAgo >= birthDate;
}

/**
 * 日付の整形 ハイフン区切りからスラッシュ区切りへ
 * @export
 * @param {string}origin 整形元の文字列: 2022-01-23
 * @returns {string} 整形済み日付: 2022/1/23
 */
export function formatDateStringHyphenToSlash(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 10) {
    return origin;
  }
  const originDate: Date = new Date(origin);
  // 9999-12-31は「-」にする
  if (isBeforeStartDate(originDate)) {
    return '-';
  }
  const result: string = `${originDate.getFullYear()}/${
    originDate.getMonth() + 1
  }/${originDate.getDate()}`;
  return result;
}

/**
 * 日付の整形
 * @export
 * @param {string}origin 整形元の文字列: 20220123
 * @returns {string} 整形済み日付: 2022/01/23
 */
export function formatDateStringYYYYMMDD(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 8) {
    return origin;
  }
  const year = parseInt(origin.substring(0, 4), 10);
  const month = parseInt(origin.substring(4, 6), 10);
  const day = parseInt(origin.substring(6, 8), 10);
  const originDate: Date = new Date(year, month - 1, day);
  return formatDatePadding(originDate);
}

/**
 * 年月の整形
 * @export
 * @param {string}origin 整形元の文字列: 2022-01
 * @returns {string} 整形済み年月: 2022年1月
 */
export function formatYearMonth(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 7) {
    return origin;
  }
  const originDate: Date = new Date(origin);
  const result: string = `${originDate.getFullYear()}年${originDate.getMonth() + 1}月`;
  return result;
}

/**
 * 日付の整形
 * @export
 * @param {Date}origin 日付
 * @returns {string} 整形済み日付: 2022/1/23
 */
export function formatDateDefault(origin: Date): string {
  const result: string = `${origin.getFullYear()}/${origin.getMonth() + 1}/${origin.getDate()}`;
  return result;
}

/**
 * 日付の整形
 * @export
 * @param {Date}origin 日付
 * @returns {string} 整形済み日付: 2022/01/23
 */
export function formatDatePadding(origin: Date): string {
  const yyyy = origin.getFullYear();
  const mm = `0${origin.getMonth() + 1}`.slice(-2);
  const dd = `0${origin.getDate()}`.slice(-2);
  const result: string = `${yyyy}/${mm}/${dd}`;
  return result;
}

/**
 * 日付の整形
 * @export
 * @param {Date}origin 日付
 * @returns {string} 整形済み日付: 2022-01-23
 */
export function formatDateHyphen(origin: Date): string {
  const yyyy = origin.getFullYear();
  const mm = `0${origin.getMonth() + 1}`.slice(-2);
  const dd = `0${origin.getDate()}`.slice(-2);
  const result: string = `${yyyy}-${mm}-${dd}`;
  return result;
}

/**
 * オプションデータの存在チェック
 * @export
 * @param {string}optionCode オプションコード: MO343N
 * @param {'appeals' | 'prices' | 'annotations'}dataName データ名: appeals
 * @returns {boolean} 存在有無: true
 */
export const checkOptionDataLength = (
  optionCode: string,
  dataName: 'appeals' | 'prices' | 'annotations'
) => {
  // options.json に対象オプション自体が無ければ、表示データなしとして扱う。
  const optionData = optionsData.options.find((data) => data.code === optionCode);
  if (optionData === undefined) {
    return false;
  }
  if (optionData[dataName].length === 0) {
    return false;
  }
  return true;
};

/**
 * 年月の整形
 * @export
 * @param {string}origin 整形元の文字列: 202201
 * @returns {string} 整形済み年月: 2022年1月
 */
export function formatYYYYMM(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 6) {
    return origin;
  }
  const year = parseInt(origin.substring(0, 4), 10);
  const month = parseInt(origin.substring(4, 6), 10);
  const originDate: Date = new Date(year, month - 1);
  const result: string = `${originDate.getFullYear()}年${originDate.getMonth() + 1}月`;
  return result;
}

/**
 * 日付の整形
 * @export
 * @param {string}origin 整形元の文字列: 2022-01-23
 * @returns {string} 整形済み日付: 2022年1月
 */
export function formatDateStringYearMonth(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 10) {
    return origin;
  }
  const originDate: Date = new Date(origin);
  // 9999-12-31は「-」にする
  if (isBeforeStartDate(originDate)) {
    return '-';
  }
  const result: string = `${originDate.getFullYear()}年${originDate.getMonth() + 1}月`;
  return result;
}

/**
 * オプションデータの存在チェック
 * @export
 * @param {string}optionCode オプションコード: MO343N
 * @param {'appeals' | 'prices' | 'annotations'}dataName データ名: appeals
 * @returns {boolean} 存在有無: true
 */
export const checkOptionMobileDataLength = (
  optionCode: string,
  dataName: 'appeals' | 'prices' | 'annotations'
) => {
  // モバイル向けは別JSONを持つため、固定回線と参照先を分ける。
  const optionData = optionsMobileData.options.find((data) => data.code === optionCode);
  if (optionData === undefined) {
    return false;
  }
  if (optionData[dataName].length === 0) {
    return false;
  }
  return true;
};

/**
 * 性別の和名変更
 * @export
 * @param {string}gender 性別 male | female | other
 * @returns {string} 性別の和名
 */
export function changeGenderHiragana(gender: string | undefined): string {
  let genderHiragana = '';

  // API の英語コードを、画面共通の和文表記へ変換する。
  if (gender === 'male') {
    genderHiragana = Constants.ContractInfo.Gender.MALE;
  } else if (gender === 'female') {
    genderHiragana = Constants.ContractInfo.Gender.FEMALE;
  } else if (gender === 'other') {
    genderHiragana = Constants.ContractInfo.Gender.OTHER;
  }

  return genderHiragana;
}

/**
 * 性別コードを日本語ラベルに変換
 * @export
 * @param {string} gender 性別コード: 1 | 2 | 3
 * @returns {string} 性別ラベル: 男性 | 女性 | 未定義
 */
export function getGenderLabel(gender: string): string {
  const genderMap: Record<string, string> = {
    '1': '男性',
    '2': '女性',
    '3': '未定義',
  };
  return genderMap[gender] || gender;
}

/**
 * 関係コードを日本語ラベルに変換
 * @export
 * @param {string} relation 関係コード: SPOUSE | CHILD | PARENT | GRD_PARENT | GRD_CHILD | PARTNER | OTHER
 * @returns {string} 関係ラベル: 配偶者 | お子さま | 親 | 祖父母 | 孫 | パートナー | その他
 */
export function getRelationLabel(relation: string): string {
  const relationMap: Record<string, string> = {
    SPOUSE: '配偶者',
    CHILD: 'お子さま',
    PARENT: '親',
    GRD_PARENT: '祖父母',
    GRD_CHILD: '孫',
    PARTNER: 'パートナー',
    OTHER: 'その他',
  };
  return relationMap[relation] || relation;
}

/**
 * 携帯番号の整形
 * @export
 * @param {string}phoneNumber 整形元の郵便番号: 12345678901
 * @returns {string} 整形済み郵便番号: 123-4567-8901
 */
export function formatPhoneNumber(phoneNumber: string): string {
  if (isEmpty(phoneNumber) === true || phoneNumber.length < 11) {
    return '';
  }
  const result: string = `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 7)}-${phoneNumber.slice(
    7
  )}`;
  return result;
}

/**
 * 全角文字を半角文字に変換する。
 * @param {string} 全角文字
 * @returns {string} 半角文字
 */
export function toHankaku(str: string): string {
  let converted = str;
  // カナと英数字は変換規則が異なるため、段階的に変換する。
  converted = toHankakuKana(converted);
  converted = toHalfAlphaNumber(converted);
  // NaN対応
  return Number.isNaN(converted) ? str : converted;
}

/** 半角カナに寄せたい入力欄向けに、全角カナを半角カナへ変換する。 */
export function toHankakuKana(str: string): string {
  const kanaMap = {
    ガ: 'ｶﾞ',
    ギ: 'ｷﾞ',
    グ: 'ｸﾞ',
    ゲ: 'ｹﾞ',
    ゴ: 'ｺﾞ',
    ザ: 'ｻﾞ',
    ジ: 'ｼﾞ',
    ズ: 'ｽﾞ',
    ゼ: 'ｾﾞ',
    ゾ: 'ｿﾞ',
    ダ: 'ﾀﾞ',
    ヂ: 'ﾁﾞ',
    ヅ: 'ﾂﾞ',
    デ: 'ﾃﾞ',
    ド: 'ﾄﾞ',
    バ: 'ﾊﾞ',
    ビ: 'ﾋﾞ',
    ブ: 'ﾌﾞ',
    ベ: 'ﾍﾞ',
    ボ: 'ﾎﾞ',
    パ: 'ﾊﾟ',
    ピ: 'ﾋﾟ',
    プ: 'ﾌﾟ',
    ペ: 'ﾍﾟ',
    ポ: 'ﾎﾟ',
    ヴ: 'ｳﾞ',
    ヷ: 'ﾜﾞ',
    ヺ: 'ｦﾞ',
    ア: 'ｱ',
    イ: 'ｲ',
    ウ: 'ｳ',
    エ: 'ｴ',
    オ: 'ｵ',
    カ: 'ｶ',
    キ: 'ｷ',
    ク: 'ｸ',
    ケ: 'ｹ',
    コ: 'ｺ',
    サ: 'ｻ',
    シ: 'ｼ',
    ス: 'ｽ',
    セ: 'ｾ',
    ソ: 'ｿ',
    タ: 'ﾀ',
    チ: 'ﾁ',
    ツ: 'ﾂ',
    テ: 'ﾃ',
    ト: 'ﾄ',
    ナ: 'ﾅ',
    ニ: 'ﾆ',
    ヌ: 'ﾇ',
    ネ: 'ﾈ',
    ノ: 'ﾉ',
    ハ: 'ﾊ',
    ヒ: 'ﾋ',
    フ: 'ﾌ',
    ヘ: 'ﾍ',
    ホ: 'ﾎ',
    マ: 'ﾏ',
    ミ: 'ﾐ',
    ム: 'ﾑ',
    メ: 'ﾒ',
    モ: 'ﾓ',
    ヤ: 'ﾔ',
    ユ: 'ﾕ',
    ヨ: 'ﾖ',
    ラ: 'ﾗ',
    リ: 'ﾘ',
    ル: 'ﾙ',
    レ: 'ﾚ',
    ロ: 'ﾛ',
    ワ: 'ﾜ',
    ヲ: 'ｦ',
    ン: 'ﾝ',
    ァ: 'ｧ',
    ィ: 'ｨ',
    ゥ: 'ｩ',
    ェ: 'ｪ',
    ォ: 'ｫ',
    ッ: 'ｯ',
    ャ: 'ｬ',
    ュ: 'ｭ',
    ョ: 'ｮ',
    '。': '｡',
    '、': '､',
    ー: 'ｰ',
    '「': '｢',
    '」': '｣',
    '・': '･',
  };
  const reg = new RegExp(`(${Object.keys(kanaMap).join('|')})`, 'g');
  return str
    .replace(reg, (match) => {
      // @ts-ignore
      return kanaMap[match];
    })
    .replace(/゛/g, 'ﾞ')
    .replace(/゜/g, 'ﾟ');
}

export function toHalfAlphaNumber(str: string): string {
  // 全角英数字記号をまず ASCII 領域へ戻し、その後例外記号だけ個別補正する。
  const halfAlphaNumber = str.replace(/[！-～]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
  });

  return halfAlphaNumber
    .replace(/’/g, "'")
    .replace(/”/g, '"')
    .replace(/‘/g, '`')
    .replace(/￥/g, '\\')
    .replace(/　/g, ' ')
    .replace(/〜/g, '~');
}

/**
 * 半角文字を全角文字に変換する。
 * @param {string} 半角文字
 * @returns {string} 全角文字
 */
export function toZenkaku(str: string): string {
  let converted = str;
  // 外部連携先で全角前提のケースがあるため、記号・ASCII・カナを順に正規化する。
  converted = toZenkakuAlphaNumMark(converted);
  converted = toFullWidth(converted);
  converted = toZenkakuKana(converted);
  // NaN対応
  return Number.isNaN(converted) ? str : converted;
}

/** 記号や英数字を全角へ寄せ、外部連携先の入力制約に合わせる。 */
export function toZenkakuAlphaNumMark(str: string): string {
  return str
    .replace(/ /g, '　')
    .replace(/"/g, '”')
    .replace(/'/g, '’')
    .replace(/`/g, '‘')
    .replace(/\\/g, '￥')
    .replace(/~/g, '～')
    .replace(/[!-~]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
    });
}

/** 半角カナを全角カナへ戻し、画面表示や送信条件に合わせる。 */
export function toZenkakuKana(str: string): string {
  const map = {
    ｶﾞ: 'ガ',
    ｷﾞ: 'ギ',
    ｸﾞ: 'グ',
    ｹﾞ: 'ゲ',
    ｺﾞ: 'ゴ',
    ｻﾞ: 'ザ',
    ｼﾞ: 'ジ',
    ｽﾞ: 'ズ',
    ｾﾞ: 'ゼ',
    ｿﾞ: 'ゾ',
    ﾀﾞ: 'ダ',
    ﾁﾞ: 'ヂ',
    ﾂﾞ: 'ヅ',
    ﾃﾞ: 'デ',
    ﾄﾞ: 'ド',
    ﾊﾞ: 'バ',
    ﾋﾞ: 'ビ',
    ﾌﾞ: 'ブ',
    ﾍﾞ: 'ベ',
    ﾎﾞ: 'ボ',
    ﾊﾟ: 'パ',
    ﾋﾟ: 'ピ',
    ﾌﾟ: 'プ',
    ﾍﾟ: 'ペ',
    ﾎﾟ: 'ポ',
    ｳﾞ: 'ヴ',
    ﾜﾞ: 'ヷ',
    ｦﾞ: 'ヺ',
    ｱ: 'ア',
    ｲ: 'イ',
    ｳ: 'ウ',
    ｴ: 'エ',
    ｵ: 'オ',
    ｶ: 'カ',
    ｷ: 'キ',
    ｸ: 'ク',
    ｹ: 'ケ',
    ｺ: 'コ',
    ｻ: 'サ',
    ｼ: 'シ',
    ｽ: 'ス',
    ｾ: 'セ',
    ｿ: 'ソ',
    ﾀ: 'タ',
    ﾁ: 'チ',
    ﾂ: 'ツ',
    ﾃ: 'テ',
    ﾄ: 'ト',
    ﾅ: 'ナ',
    ﾆ: 'ニ',
    ﾇ: 'ヌ',
    ﾈ: 'ネ',
    ﾉ: 'ノ',
    ﾊ: 'ハ',
    ﾋ: 'ヒ',
    ﾌ: 'フ',
    ﾍ: 'ヘ',
    ﾎ: 'ホ',
    ﾏ: 'マ',
    ﾐ: 'ミ',
    ﾑ: 'ム',
    ﾒ: 'メ',
    ﾓ: 'モ',
    ﾔ: 'ヤ',
    ﾕ: 'ユ',
    ﾖ: 'ヨ',
    ﾗ: 'ラ',
    ﾘ: 'リ',
    ﾙ: 'ル',
    ﾚ: 'レ',
    ﾛ: 'ロ',
    ﾜ: 'ワ',
    ｦ: 'ヲ',
    ﾝ: 'ン',
    ｧ: 'ァ',
    ｨ: 'ィ',
    ｩ: 'ゥ',
    ｪ: 'ェ',
    ｫ: 'ォ',
    ｯ: 'ッ',
    ｬ: 'ャ',
    ｭ: 'ュ',
    ｮ: 'ョ',
    '｡': '。',
    '､': '、',
    ｰ: 'ー',
    '｢': '「',
    '｣': '」',
    '･': '・',
  };
  const reg = new RegExp(`(${Object.keys(map).join('|')})`, 'g');
  return str.replace(reg, (match) => {
    // @ts-ignore
    return map[match];
  });
}

export function toFullWidth(str: string): string {
  return str.replace(/[!-~]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
  });
}

/**
 * 全角から半角に変換
 */
export const toHalfWidth = (value: string): string => {
  return value
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0))
    .replace(/\u3000/g, ' '); // 全角スペースを半角スペースに変換
};
